import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'antd';
import * as Yup from 'yup';
import { Form, notify } from '@/components';
import { formatters, YupValue } from '@/core';
import { FormUserAvatarEdit } from '@/views';
import { useGetProfileQuery, useUpdateProfileMutation } from '@/redux';
import classes from './UserProfilePanel.module.scss';

const schema = Yup.object().shape({
  email: Yup.string().required(),
  firstName: Yup.string().field().nullable().required(),
  lastName: Yup.string().field().nullable().required(),
  avatar: Yup.string().nullable(),
});

type FormValue = YupValue<typeof schema>;

const useSubmit = () => {
  const { data } = useGetProfileQuery();
  const [update] = useUpdateProfileMutation();
  const { id } = data ?? {};

  return useCallback(
    async (values: FormValue) =>
      await update({ ...values, id: id! })
        .unwrap()
        .then(() => notify.success.t('users.profile.saved')),
    [update, id],
  );
};

export function UserProfilePanel() {
  const { t } = useTranslation();
  const { data } = useGetProfileQuery();
  const onSubmit = useSubmit();

  if (!data) {
    return null;
  }

  const { firstName, lastName, avatar, email } = data;

  return (
    <div>
      <Form.Formik<FormValue>
        uid="user-profile"
        i18nKeyPrefix="users.profile"
        initialValues={{ firstName, lastName, avatar, email }}
        validationSchema={schema}
        onSubmit={onSubmit}
      >
        <Row className={classes.userProfilePanel}>
          <Col xs={24} md={1}>
            <div className={classes.avatarWrapper}>
              <FormUserAvatarEdit fullName={formatters.name(data)} name="avatar" />
            </div>
          </Col>
          <Col
            xs={24}
            md={{ span: 14, offset: 4 }}
            xl={{ span: 8, offset: 2 }}
            className={classes.formsWrapper}
          >
            <Form.Text name="email" />
            <Form.Input name="firstName" required />
            <Form.Input name="lastName" required />
            <div className={classes.submitBtnWrapper}>
              <Form.Submit size="large" type="primary" uid="user-profile">
                {t('save')}
              </Form.Submit>
            </div>
          </Col>
        </Row>
      </Form.Formik>
    </div>
  );
}
